/*
    A picture read off its atlas page for a bake (0.98.8): the parts without Pixi, shared by the page (managers/
    atlasReads.ts) and the bakes' worker (managers/atlasReadWorker.ts), so both read a picture the same way, as the
    normal maps (managers/normalLook.ts) and the snow caps (managers/snowLook.ts) always have: its frame cut out of the
    page, turned back as the packer keeps it (a quarter turn) and set in its own frame (the packer trims a picture's
    empty border), drawn at the bake's size on a canvas of its own, and read back.
*/

/** how a picture is read off its page: the canvas's transform, the frame's place on the page, what it's drawn as */
export interface AtlasRead {
    /** the canvas's transform (`setTransform(a, b, c, d, e, f)`) */
    readonly a: number
    readonly b: number
    readonly c: number
    readonly d: number
    readonly e: number
    readonly f: number
    /** the frame on the page, in the page's own pixels (the frame times its source's resolution) */
    readonly sx: number
    readonly sy: number
    readonly sw: number
    readonly sh: number
    /** what the frame is drawn as, in the picture's pixels (before the transform) */
    readonly dw: number
    readonly dh: number
    /** the bake's size (pixels): the canvas's */
    readonly w: number
    readonly h: number
}

/** a frame on its page, as Pixi keeps it (its `frame`, `trim` and `rotate`, the source's `resolution`) */
export interface AtlasFrame {
    readonly frame: { readonly x: number, readonly y: number, readonly width: number, readonly height: number }
    readonly trim?: { readonly x: number, readonly y: number } | null
    readonly rotate: number
    readonly resolution: number
}

/**
 * the read of a picture `width` by `height` (its own size, untrimmed) at the bake's `w` by `h`: kept turned a quarter
 * on the page (`rotate` 2), the page's region (u across and v down) lies in the picture at x = v, y = (the region's
 * width) - u, as Pixi reads it back; the picture's own pixels to the bake's: drawn small, each side rounded to whole pixels
 */
export function atlasRead(at: AtlasFrame, width: number, height: number, w: number, h: number): AtlasRead {
    const { frame, trim } = at;
    const res = at.resolution || 1;
    const tx = trim?.x ?? 0, ty = trim?.y ?? 0;
    const kx = w / width, ky = h / height;
    const turned = at.rotate === 2;
    return {
        a: turned ? 0 : kx,
        b: turned ? -ky : 0,
        c: turned ? kx : 0,
        d: turned ? 0 : ky,
        e: kx * tx,
        f: turned ? ky * (ty + frame.width) : ky * ty,
        sx: frame.x * res,
        sy: frame.y * res,
        sw: frame.width * res,
        sh: frame.height * res,
        dw: frame.width,
        dh: frame.height,
        w,
        h
    };
}

/** the picture drawn on `ctx` (a canvas of the read's size, its own) off `page` as the read says, and its pixels read back */
export function drawAtlasRead(ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, page: CanvasImageSource, read: AtlasRead): ImageData {
    ctx.save();
    ctx.setTransform(read.a, read.b, read.c, read.d, read.e, read.f);
    ctx.drawImage(page, read.sx, read.sy, read.sw, read.sh, 0, 0, read.dw, read.dh);
    ctx.restore();
    return ctx.getImageData(0, 0, read.w, read.h);
}

/**
 * the mipmap level a picture is drawn from (0.98.8): drawn under half its size each way (a roof, a floor, a big thing:
 * a bake's longest side is 256 pixels), the browser draws an image from a smaller copy of it, not from its own pixels
 * (Chromium's `MipMapUtil`: the copy halved as often as the bake's size still fits in the next half). 0: the picture
 * itself. The bakes' worker draws from a decoded bitmap, which the browser draws from its own pixels at any size, so
 * it makes that copy itself (`drawAtlasReadAt`)
 */
export function readLevel(read: AtlasRead): number {
    // (the bake's scale of the frame along each of its axes, its page's pixels to the bake's)
    const scaleX = Math.hypot(read.a, read.b) * (read.dw / read.sw);
    const scaleY = Math.hypot(read.c, read.d) * (read.dh / read.sh);
    const sw = Math.max(1, Math.round(read.sw)), sh = Math.max(1, Math.round(read.sh));
    const tw = Math.ceil(sw * scaleX), th = Math.ceil(sh * scaleY);
    for (let level = 0; ; level++) {
        const w = Math.max(1, sw >> level), h = Math.max(1, sh >> level);
        const nw = Math.max(1, sw >> (level + 1)), nh = Math.max(1, sh >> (level + 1));
        if ((w === 1 && h === 1) || nw < tw || nh < th) return level;
    }
}

/**
 * a premultiplied picture (RGBA bytes, `w` by `h`) halved each way, as Skia makes a mipmap's next level: each pixel
 * the mean of the 2 by 2 under it, an odd side's three with the middle twice (1, 2, 1), rounded down
 */
export function halve(pixels: Uint8ClampedArray, w: number, h: number): { readonly pixels: Uint8ClampedArray, readonly w: number, readonly h: number } {
    const w1 = Math.max(1, w >> 1), h1 = Math.max(1, h >> 1);
    const out = new Uint8ClampedArray(w1 * h1 * 4);
    const oddX = w > 1 && (w & 1) === 1, oddY = h > 1 && (h & 1) === 1;
    const at = (x: number, y: number, c: number): number => pixels[(Math.min(h - 1, y) * w + Math.min(w - 1, x)) * 4 + c];
    for (let y = 0; y < h1; y++) {
        const y0 = y * 2;
        for (let x = 0; x < w1; x++) {
            const x0 = x * 2;
            for (let c = 0; c < 4; c++) {
                let sum: number, shift: number;
                if (!oddX && !oddY) {
                    sum = at(x0, y0, c) + at(x0 + 1, y0, c) + at(x0, y0 + 1, c) + at(x0 + 1, y0 + 1, c);
                    shift = 2;
                } else if (oddX && !oddY) {
                    sum = at(x0, y0, c) + 2 * at(x0 + 1, y0, c) + at(x0 + 2, y0, c) + at(x0, y0 + 1, c) + 2 * at(x0 + 1, y0 + 1, c) + at(x0 + 2, y0 + 1, c);
                    shift = 3;
                } else if (!oddX && oddY) {
                    sum = at(x0, y0, c) + 2 * at(x0, y0 + 1, c) + at(x0, y0 + 2, c) + at(x0 + 1, y0, c) + 2 * at(x0 + 1, y0 + 1, c) + at(x0 + 1, y0 + 2, c);
                    shift = 3;
                } else {
                    const row = (yy: number): number => at(x0, yy, c) + 2 * at(x0 + 1, yy, c) + at(x0 + 2, yy, c);
                    sum = row(y0) + 2 * row(y0 + 1) + row(y0 + 2);
                    shift = 4;
                }
                out[(y * w1 + x) * 4 + c] = sum >> shift;
            }
        }
    }
    return { pixels: out, w: w1, h: h1 };
}

/** straight RGBA to premultiplied (in place), each colour times its alpha, rounded */
export function premultiply(pixels: Uint8ClampedArray): Uint8ClampedArray {
    for (let i = 0; i < pixels.length; i += 4) {
        const a = pixels[i + 3];
        if (a === 255) continue;
        pixels[i] = Math.round(pixels[i] * a / 255);
        pixels[i + 1] = Math.round(pixels[i + 1] * a / 255);
        pixels[i + 2] = Math.round(pixels[i + 2] * a / 255);
    }
    return pixels;
}

/** premultiplied RGBA back to straight (in place) */
export function unpremultiply(pixels: Uint8ClampedArray): Uint8ClampedArray {
    for (let i = 0; i < pixels.length; i += 4) {
        const a = pixels[i + 3];
        if (a === 255) continue;
        if (a === 0) {
            pixels[i] = pixels[i + 1] = pixels[i + 2] = 0;
            continue;
        }
        pixels[i] = Math.min(255, Math.round(pixels[i] * 255 / a));
        pixels[i + 1] = Math.min(255, Math.round(pixels[i + 1] * 255 / a));
        pixels[i + 2] = Math.min(255, Math.round(pixels[i + 2] * 255 / a));
    }
    return pixels;
}

/** the frame's whole pixels on its page round its place (a frame at half pixels, a half-size page's, taken whole) */
export function frameBox(read: AtlasRead): { readonly x: number, readonly y: number, readonly w: number, readonly h: number } {
    const x = Math.floor(read.sx), y = Math.floor(read.sy);
    return { x, y, w: Math.max(1, Math.ceil(read.sx + read.sw) - x), h: Math.max(1, Math.ceil(read.sy + read.sh) - y) };
}

/**
 * a picture read off its page as a browser draws an image (`drawAtlasRead`), drawn from the page's decoded bitmap
 * (the bakes' worker): under half its size, from its frame's copy at the mipmap level the browser would take
 * (`readLevel`, `halve`), its place on the copy as the frame's on the page; `canvas` makes a canvas of a size
 */
export function drawAtlasReadAt(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    page: CanvasImageSource,
    read: AtlasRead,
    canvas: (w: number, h: number) => OffscreenCanvas
): ImageData {
    const level = readLevel(read);
    if (level === 0) return drawAtlasRead(ctx, page, read);
    // the frame's own pixels, as they are (drawn at their size on whole pixels: copied, not filtered)
    const box = frameBox(read);
    const whole = canvas(box.w, box.h);
    const wctx = whole.getContext("2d", { willReadFrequently: true });
    if (!wctx) throw new Error("no 2D context");
    wctx.drawImage(page, box.x, box.y, box.w, box.h, 0, 0, box.w, box.h);
    let copy = { pixels: premultiply(wctx.getImageData(0, 0, box.w, box.h).data), w: box.w, h: box.h };
    for (let i = 0; i < level; i++) copy = halve(copy.pixels, copy.w, copy.h);
    const smaller = canvas(copy.w, copy.h);
    const sctx = smaller.getContext("2d", { willReadFrequently: true });
    if (!sctx) throw new Error("no 2D context");
    const image = sctx.createImageData(copy.w, copy.h);
    image.data.set(unpremultiply(copy.pixels));
    sctx.putImageData(image, 0, 0);
    // (the frame's place on the copy: its place on the page, the copy's pixels to the page's)
    const kx = copy.w / box.w, ky = copy.h / box.h;
    return drawAtlasRead(ctx, smaller, { ...read, sx: (read.sx - box.x) * kx, sy: (read.sy - box.y) * ky, sw: read.sw * kx, sh: read.sh * ky });
}
